import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type Ambassador,
  type AmbassadorLoad,
  type AmbassadorLoads,
  type AmbassadorZone,
  type AssignParams,
  type QueueDelivery,
  type QueueDeliveryDetail,
  type QueueQuery,
} from "../../domain/entities/assignment-queue";
import { slotKeyOf } from "../../domain/entities/slot";
import {
  AssignRequestError,
  GetDeliveryDetailError,
  ListAmbassadorLoadsError,
  ListAmbassadorZonesError,
  ListAmbassadorsError,
  ListQueueError,
  QueryQueueError,
} from "../../domain/errors/errors";
import { type RouteAssignmentRepository } from "../../domain/repos/route-assignment-repo";
import {
  type ActiveDeliveriesResponse,
  type AmbassadorsResponse,
  type CoverageResponse,
  type DeliveryDetailResponse,
  type QueueResponse,
} from "../../infrastructure/responses/assignment-responses";

export class RouteAssignmentRepo implements RouteAssignmentRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listQueue(): Promise<QueueDelivery[]> {
    try {
      // No page_size: the backend only paginates when one is asked for, and
      // the screen needs the whole queue to count anything truthfully.
      const response = await this.apiClient.get<QueueResponse>(
        "v1/requests?state=ready_for_logistics",
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListQueueError(message, apiError);
    }
  }

  async queryQueue(params: QueueQuery): Promise<QueueDelivery[]> {
    try {
      const search = new URLSearchParams({ state: "ready_for_logistics" });
      if (params.q?.trim()) search.set("q", params.q.trim());
      if (params.dayISO) {
        // The day's local bounds, half-open: the backend compares the slot with
        // `>= from` and `< to`, so the next midnight is the right upper edge.
        const [y, m, d] = params.dayISO.split("-").map(Number);
        const from = new Date(y, (m ?? 1) - 1, d ?? 1);
        const to = new Date(y, (m ?? 1) - 1, (d ?? 1) + 1);
        search.set("delivery_slot_from", from.toISOString());
        search.set("delivery_slot_to", to.toISOString());
      }
      const response = await this.apiClient.get<QueueResponse>(
        `v1/requests?${search.toString()}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new QueryQueueError(message, apiError);
    }
  }

  async listAmbassadors(): Promise<Ambassador[]> {
    try {
      const response = await this.apiClient.get<AmbassadorsResponse>(
        "v1/users?role=ambassador",
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorsError(message, apiError);
    }
  }

  async listAmbassadorLoads(): Promise<AmbassadorLoads> {
    try {
      // The load is each ambassador's active (assigned + in_route) deliveries —
      // the backend has no aggregate for it, so read both states and bucket.
      const [assigned, inRoute] = await Promise.all([
        this.apiClient.get<ActiveDeliveriesResponse>("v1/requests?state=assigned"),
        this.apiClient.get<ActiveDeliveriesResponse>("v1/requests?state=in_route"),
      ]);
      const loads: AmbassadorLoads = {};
      for (const items of [assigned.data?.items ?? [], inRoute.data?.items ?? []]) {
        for (const item of items) {
          const id = item.assignedAmbassadorId;
          if (!id) continue;
          const load: AmbassadorLoad = loads[id] ?? { total: 0, bySlot: {} };
          load.total += 1;
          // A delivery with no schedule still counts against the total — it is
          // work the ambassador holds — but it belongs to no day and no shift,
          // so it lands in no bucket.
          const key = slotKeyOf(item.deliverySlotAt, item.deliveryShift);
          if (key) load.bySlot[key] = (load.bySlot[key] ?? 0) + 1;
          loads[id] = load;
        }
      }
      return loads;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorLoadsError(message, apiError);
    }
  }

  async listAmbassadorZones(): Promise<AmbassadorZone[]> {
    try {
      // The whole catalogue, inactive included: an ambassador assigned to a
      // zone that was later switched off still has a name worth printing.
      const response = await this.apiClient.get<CoverageResponse>("v1/coverage");
      // A zone with no id or no name cannot be matched or printed, so it is
      // dropped rather than allowed to break the whole cascade: these options
      // are the only way to filter by geography at all.
      return (response.data?.items ?? [])
        .filter((z) => z?.locationId && z?.name)
        .map((z) => ({
          locationId: z.locationId,
          name: z.name,
          municipalityName: z.municipalityName ?? "",
          departmentName: z.departmentName ?? "",
        }));
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorZonesError(message, apiError);
    }
  }

  async getDeliveryDetail(requestId: string): Promise<QueueDeliveryDetail> {
    try {
      const response = await this.apiClient.get<DeliveryDetailResponse>(
        `v1/requests/${requestId}`,
      );
      const detail = response.data;
      if (!detail) throw new GetDeliveryDetailError("empty response");
      return detail;
    } catch (error) {
      if (error instanceof GetDeliveryDetailError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new GetDeliveryDetailError(message, apiError);
    }
  }

  async assign(params: AssignParams): Promise<void> {
    try {
      // ambassador_id is the target; the assigning supervisor is the session user.
      await this.apiClient.post(`v1/requests/${params.requestId}/assignment`, {
        ambassadorId: params.ambassadorId,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new AssignRequestError(message, apiError);
    }
  }
}
